/**
 * Phase 14 — runtime service-line label resolver.
 *
 * Backend codes (`GHA`, `GHBC`, `GLH`, `TVA`, `FH`) are persistent IDs.
 * Display labels live in the `service_lines` Mongo collection and are
 * fetched once via `GET /api/service-lines`. This hook caches the result
 * in module memory so all dropdowns / chips / charts render the human
 * label without each component fetching independently.
 *
 * Falls back to the code itself if the lookup hasn't loaded yet (or the
 * code isn't in the active list, which can happen for archived rows on
 * historical data).
 */
import { useEffect, useState } from "react";
import api from "./api";

let _cache = null;
let _inflight = null;

export async function fetchServiceLines() {
  if (_cache) return _cache;
  if (_inflight) return _inflight;
  _inflight = api
    .get("/service-lines")
    .then((res) => {
      _cache = Array.isArray(res.data) ? res.data : [];
      return _cache;
    })
    .catch(() => {
      _cache = [];
      return _cache;
    })
    .finally(() => {
      _inflight = null;
    });
  return _inflight;
}

/** Hook that returns `{lines, nameOf, ready}`. */
export function useServiceLines() {
  const [lines, setLines] = useState(_cache || []);
  const [ready, setReady] = useState(_cache !== null);

  useEffect(() => {
    let cancelled = false;
    fetchServiceLines().then((data) => {
      if (cancelled) return;
      setLines(data);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const map = new Map((lines || []).map((l) => [l.code, l.name]));
  const nameOf = (code) => map.get(code) || code || "";
  return { lines, nameOf, ready };
}

/** Synchronous helper for components that already have `lines`. */
export function nameOfSync(lines, code) {
  if (!Array.isArray(lines)) return code;
  const hit = lines.find((l) => l.code === code);
  return hit ? hit.name : code;
}
